import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { viewReportedBookingDetails } from '../../slice/reportedBookingsSlice'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment'

const STATUS_BADGE = {
    in_review: { label: 'In Review', color: '#fd7e14', bg: '#fff3e0' },
    resolved:  { label: 'Resolved',  color: '#198754', bg: '#e8f5e9' },
    rejected:  { label: 'Rejected',  color: '#dc3545', bg: '#fdecea' },
    pending:   { label: 'Pending',   color: '#6c757d', bg: '#f1f3f5' },
}

const BOOKING_STATUS_BADGE = {
    ongoing:   { label: 'Ongoing',   cls: 'bg-primary' },
    completed: { label: 'Completed', cls: 'bg-success' },
    cancelled: { label: 'Cancelled', cls: 'bg-danger'  },
    pending:   { label: 'Pending',   cls: 'bg-warning text-dark' },
}

const ViewReportedBookingDetail = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()

    const reportId = location.state?.reportId

    const { reportedBookingDetail } = useSelector(state => state.reportedBooking)

    useEffect(() => {
        if (!reportId) {
            navigate('/reported-bookings')
            return
        }
        dispatch(viewReportedBookingDetails({ id: reportId }))
    }, [reportId, dispatch, navigate])

    const {
        id,
        reason,
        reported_on,
        status,
        admin_notes,
        is_active,
        is_deleted,
        createdAt,
        updatedAt,
        reported_by_user_type,
        booking,
    } = reportedBookingDetail

    const reportBadge = STATUS_BADGE[status] || STATUS_BADGE['pending']
    const bookingBadge = BOOKING_STATUS_BADGE[booking?.status] || { label: booking?.status || 'NA', cls: 'bg-secondary' }

    const tutor = booking?.tutor
    const parent = booking?.parent

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Reported Booking Details</h4>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/reported-bookings')}
                    style={{ padding: '8px 20px', fontSize: '14px', borderRadius: '6px' }}
                >
                    ← Back to List
                </button>
            </div>

            {/* Summary Card */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '8px' }}>
                <div className="card-body p-4 d-flex align-items-center gap-4 flex-wrap">
                    <div
                        style={{
                            width: '64px',
                            height: '64px',
                            borderRadius: '12px',
                            backgroundColor: '#fff3e0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <i className="fe-flag" style={{ fontSize: '28px', color: '#fd7e14' }} />
                    </div>

                    <div>
                        <h5 className="mb-1 fw-semibold">Report #{id}</h5>
                        <p className="mb-2 text-muted" style={{ fontSize: '14px' }}>
                            Booking: <strong>{booking?.booking_id || 'NA'}</strong>
                        </p>
                        <div className="d-flex gap-2 flex-wrap">
                            <span style={{
                                display: 'inline-block',
                                padding: '3px 10px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                color: reportBadge.color,
                                backgroundColor: reportBadge.bg,
                            }}>
                                {reportBadge.label}
                            </span>
                            <span className={`badge ${is_active ? 'bg-success' : 'bg-secondary'}`}>
                                {is_active ? 'Active' : 'Inactive'}
                            </span>
                            {is_deleted === 1 && (
                                <span className="badge bg-danger">Deleted</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                {/* Report Info */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Report Information
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Reported By (Type)</small>
                                <strong>
                                    {reported_by_user_type
                                        ? reported_by_user_type.charAt(0).toUpperCase() + reported_by_user_type.slice(1)
                                        : 'NA'}
                                </strong>
                            </div>
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Reason</small>
                                <strong>{reason || 'NA'}</strong>
                            </div>
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Reported On</small>
                                <strong>
                                    {reported_on
                                        ? moment(reported_on).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </strong>
                            </div>
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Report Status</small>
                                <span style={{
                                    display: 'inline-block',
                                    padding: '3px 10px',
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: reportBadge.color,
                                    backgroundColor: reportBadge.bg,
                                }}>
                                    {reportBadge.label}
                                </span>
                            </div>
                            <div>
                                <small className="text-muted d-block mb-1">Admin Notes</small>
                                <strong>{admin_notes || <span className="text-muted fst-italic">No notes added</span>}</strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Booking Info */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Booking Information
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Booking ID</small>
                                <strong>{booking?.booking_id || 'NA'}</strong>
                            </div>
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Booking Status</small>
                                <span className={`badge ${bookingBadge.cls}`}>{bookingBadge.label}</span>
                            </div>
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Start Time</small>
                                <strong>
                                    {booking?.start_time
                                        ? moment(booking.start_time).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </strong>
                            </div>
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">End Time</small>
                                <strong>
                                    {booking?.end_time
                                        ? moment(booking.end_time).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </strong>
                            </div>
                            <div>
                                <small className="text-muted d-block mb-1">Session Accepted</small>
                                <strong>{booking?.is_accepted === 1 ? 'Yes' : 'No'}</strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tutor Info */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Tutor Information
                        </div>
                        <div className="card-body">
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <img
                                    src={tutor?.profile_picture || '/assets/avatar.png'}
                                    alt="Tutor"
                                    onError={(e) => {
                                        e.currentTarget.onerror = null
                                        e.currentTarget.src = '/assets/avatar.png'
                                    }}
                                    style={{
                                        width: '56px',
                                        height: '56px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        border: '2px solid #f8f9fa',
                                        flexShrink: 0,
                                    }}
                                />
                                <div>
                                    <div className="fw-semibold" style={{ fontSize: '15px' }}>
                                        {`${tutor?.first_name || ''} ${tutor?.last_name || ''}`.trim() || 'NA'}
                                    </div>
                                    {tutor?.is_premium_verified === 1 && (
                                        <span className="badge bg-warning text-dark mt-1" style={{ fontSize: '11px' }}>
                                            Premium Verified
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Email</small>
                                <strong>{tutor?.email || 'NA'}</strong>
                            </div>
                            <div>
                                <small className="text-muted d-block mb-1">Phone</small>
                                <strong>
                                    {tutor?.country_code && tutor?.phone_number
                                        ? `${tutor.country_code} ${tutor.phone_number}`
                                        : 'NA'}
                                </strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Parent Info */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Parent Information
                        </div>
                        <div className="card-body">
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <img
                                    src={parent?.profile_picture || '/assets/avatar.png'}
                                    alt="Parent"
                                    onError={(e) => {
                                        e.currentTarget.onerror = null
                                        e.currentTarget.src = '/assets/avatar.png'
                                    }}
                                    style={{
                                        width: '56px',
                                        height: '56px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        border: '2px solid #f8f9fa',
                                        flexShrink: 0,
                                    }}
                                />
                                <div className="fw-semibold" style={{ fontSize: '15px' }}>
                                    {`${parent?.first_name || ''} ${parent?.last_name || ''}`.trim() || 'NA'}
                                </div>
                            </div>
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Email</small>
                                <strong>{parent?.email || 'NA'}</strong>
                            </div>
                            <div>
                                <small className="text-muted d-block mb-1">Phone</small>
                                <strong>
                                    {parent?.country_code && parent?.phone_number
                                        ? `${parent.country_code} ${parent.phone_number}`
                                        : 'NA'}
                                </strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Record Timestamps */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Record Information
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Created At</small>
                                <strong>
                                    {createdAt
                                        ? moment(createdAt).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </strong>
                            </div>
                            <div>
                                <small className="text-muted d-block mb-1">Last Updated</small>
                                <strong>
                                    {updatedAt
                                        ? moment(updatedAt).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </strong>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ViewReportedBookingDetail